import {
	Button,
	Checkbox,
	Collapse,
	Typography,
	Radio,
	Row,
	Col,
	Form,
	InputNumber,
	Slider,
	Input,
	Select,
} from 'antd';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useEffect, useState } from 'react';
import { useQueryParameters } from '../../hooks';
import { useNavigate } from 'react-router';
import { useGetBuildingsQuery } from '../../redux/api/location';
import {
	ANGLE,
	CHANNEL,
	HEX_BAR,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../config/constants';
import { getEffectiveShapeName } from '../../utils/shapeUtils';
import { useLocationLabel } from '../../utils/locationLabel';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import { Shelf } from '../../interfaces/Material.Interface';

const { Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

interface Props {
	lineItem?: Lineitem;
	search?: string;
	availability_status?: string;
	vendors: any[];
	vendors_selected: string;
	shapes: any[];
	shape_selected: string;
	categories: any[];
	category_selected: string;
	types: any[];
	types_selected: string;
	min_total_available_length: string;
	has_certificate: string;
	outer_diameter_gte: number;
	outer_diameter_lte: number;
	inner_diameter_gte: number;
	inner_diameter_lte: number;
	width_gte: number;
	width_lte: number;
	height_gte: number;
	height_lte: number;
	wall_thickness_gte: number;
	wall_thickness_lte: number;
	material_length_id: string;
	locations: any[];
	location_id_selected: string;
	loading: boolean;
	onSearchLocation: (value: string) => void;
	bundle_friendly_id?: string;
	heightRange?: { min: number; max: number };
	widthRange?: { min: number; max: number };
	totalAvailableLengthRange?: { min: number; max: number };
	outerDiameterRange?: { min: number; max: number };
	innerDiameterRange?: { min: number; max: number };
	wallThicknessRange?: { min: number; max: number };
	resetFiltersSignal?: () => void;
}

export const MaterialTableFilter = (props: Props) => {
	const queryParams = useQueryParameters();
	const navigate = useNavigate();
	const { data: buildings = [] } = useGetBuildingsQuery();
	const showBuildingFilter = buildings.length > 1;
	const formatLocation = useLocationLabel();

	const [activeKeys, setActiveKeys] = useState<string[]>(['shape']);

	const [search, setSearch] = useState<string | null | undefined>(
		props?.search
	);
	const [selectedStatus, setSelectedStatus] = useState([
		props.availability_status,
	]);
	const [selectedVendors, setSelectedVendors] = useState<string[]>(
		props.vendors_selected.split(',')
	);
	const [selectedShape, setSelectedShape] = useState<string>(
		props.shape_selected
	);
	const [selectedCategory, setSelectedCategory] = useState<string>(
		props.category_selected
	);
	const [selectedTypes, setSelectedTypes] = useState<string[]>(
		props.types_selected.split(',', 0)
	);
	const [searchTypesValue, setSearchTypesValue] = useState('');
	const [minimumTotalAvailableLength, setMinimumTotalAvailableLength] =
		useState<number | null>(Number(props.min_total_available_length));
	const [certificateFilter, setCertificateFilter] = useState<string>(() => {
		if (
			props.has_certificate === 'True' ||
			props.has_certificate === 'has_cert'
		) {
			return 'has_cert';
		} else if (
			props.has_certificate === 'False' ||
			props.has_certificate === 'no_cert'
		) {
			return 'no_cert';
		}
		return 'all';
	});
	const [outerDiameterRange, setOuterDiameterRange] = useState<
		[number, number]
	>([0, 999]);
	const [innerDiameterRange, setInnerDiameterRange] = useState<
		[number, number]
	>([0, 999]);
	const [widthRange, setWidthRange] = useState<[number, number]>([0, 999]);
	const [heightRange, setHeightRange] = useState<[number, number]>([0, 999]);

	const [wallThicknessRange, setWallThicknessRange] = useState<
		[number, number]
	>([0, 999]);

	const [materialLengthId, setMaterialLengthId] = useState<string>(
		props.material_length_id || ''
	);

	const [selectedLocations, setSelectedLocations] = useState<string[]>(
		props.location_id_selected ? props.location_id_selected.split(',') : []
	);
	const [bundleFriendlyId, setBundleFriendlyId] = useState<string>(
		props.bundle_friendly_id || ''
	);

	const handleResetFilters = () => {
		const rectangle = props.shapes.find(
			(x) => x.display_name === RECTANGLE_BAR
		);

		// setSelectedStatus(['Available']);
		setSelectedVendors([]);
		setSelectedShape(rectangle?.id);
		setSelectedCategory('');
		setSelectedTypes([]);
		setOuterDiameterRange([0, 999]);
		setInnerDiameterRange([0, 999]);
		setWidthRange([0, 999]);
		setHeightRange([0, 999]);
		setWallThicknessRange([0, 999]);
		setMinimumTotalAvailableLength(null);
		setSelectedLocations([]);
		setBundleFriendlyId('');
		setCertificateFilter('all');

		queryParams.delete('search');
		// queryParams.delete('availability_status');
		queryParams.delete('vendors');
		queryParams.set('shape', rectangle.id);
		queryParams.delete('category');
		queryParams.delete('types');
		queryParams.delete('min_total_available_length');
		queryParams.delete('has_certificate');
		queryParams.delete('outer_diameter__gte');
		queryParams.delete('outer_diameter__lte');
		queryParams.delete('inner_diameter__gte');
		queryParams.delete('inner_diameter__lte');
		queryParams.delete('width__gte');
		queryParams.delete('width__lte');
		queryParams.delete('height__gte');
		queryParams.delete('height__lte');
		queryParams.delete('wall_thickness__gte');
		queryParams.delete('wall_thickness__lte');
		queryParams.delete('material_length_id');
		queryParams.delete('location_id');
		queryParams.delete('bundle_friendly_id');
		queryParams.delete('total_length__gte');
		queryParams.delete('total_length__lte');
		queryParams.delete('material_lengths_count__gte');
		queryParams.delete('material_lengths_count__lte');
		queryParams.delete('building');

		queryParams.set('page', '1');
		// queryParams.set('availability_status', 'Available');
		navigate({ search: queryParams.toString() });
	};

	const handleSetQueryParams = (key: string, value: string) => {
		queryParams.delete('search');
		queryParams.set(key, value);
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });
	};

	const handleDeleteQueryParams = (key: string) => {
		queryParams.delete(key);
		navigate({ search: queryParams.toString() });
	};

	const handleCertificateChange = (value: string) => {
		setCertificateFilter(value);
		if (value === 'all' || value === '') {
			queryParams.delete('has_certificate');
			queryParams.delete('search');
			queryParams.set('page', '1');
			navigate({ search: queryParams.toString() });
		} else {
			queryParams.delete('search');
			queryParams.set('has_certificate', value);
			queryParams.set('page', '1');
			navigate({ search: queryParams.toString() });
		}
	};

	const handleStatusChange = (checkedValues: string[]) => {
		if (
			checkedValues.includes('Available') &&
			checkedValues.includes('Unavailable')
		) {
			const lastSelected = checkedValues[checkedValues.length - 1];
			setSelectedStatus([lastSelected]);
			handleSetQueryParams('availability_status', lastSelected);
		} else {
			setSelectedStatus(checkedValues);
			if (checkedValues.length === 0) {
				queryParams.delete('availability_status');
			}
			navigate({ search: queryParams.toString() });
		}
	};

	const handleVendorChange = (checkedValues: string[]) => {
		setSelectedVendors(checkedValues);
		handleSetQueryParams('vendors', checkedValues.join(','));
	};

	const handleBundleFriendlyIdChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		setBundleFriendlyId(e.target.value);
	};

	const handleApplyBundleFriendlyId = () => {
		const value = bundleFriendlyId.trim();
		if (value) {
			handleSetQueryParams('bundle_friendly_id', value);
		} else {
			handleDeleteQueryParams('bundle_friendly_id');
		}
	};

	const handleShapeChange = (e: any): void => {
		const value = e.target.value;
		setSelectedShape(value);
		const shapeName = getShapeNameById(value);

		if (
			shapeName === RECTANGLE_BAR ||
			shapeName === PLATE ||
			shapeName === CHANNEL ||
			shapeName === ANGLE
		) {
			queryParams.delete('outer_diameter__gte');
			queryParams.delete('outer_diameter__lte');
			queryParams.delete('inner_diameter__gte');
			queryParams.delete('inner_diameter__lte');
			queryParams.delete('wall_thickness__gte');
			queryParams.delete('wall_thickness__lte');
		} else if (shapeName === 'Round') {
			queryParams.delete('width__gte');
			queryParams.delete('width__lte');
			queryParams.delete('height__gte');
			queryParams.delete('height__lte');
			queryParams.delete('inner_diameter__gte');
			queryParams.delete('inner_diameter__lte');
			queryParams.delete('wall_thickness__gte');
			queryParams.delete('wall_thickness__lte');
		} else if (shapeName === 'Tube') {
			queryParams.delete('width__gte');
			queryParams.delete('width__lte');
			queryParams.delete('height__gte');
			queryParams.delete('height__lte');
			queryParams.delete('outer_diameter__gte');
			queryParams.delete('outer_diameter__lte');
			queryParams.delete('wall_thickness__gte');
			queryParams.delete('wall_thickness__lte');
		}

		handleSetQueryParams('shape', value);
	};

	const handleCategoryChange = (e: any) => {
		setSelectedCategory(e.target.value);
		handleSetQueryParams('category', e.target.value);
	};

	// Handle changes in the search box
	const handleSearchChange = (e) => {
		setSearchTypesValue(e.target.value);
	};

	// Filter the list of types based on the searchValue
	const filteredTypes = props.types.filter((type) =>
		type.display_name.toLowerCase().includes(searchTypesValue.toLowerCase())
	);

	// Handle changes in the checkbox group
	const handleTypeChange = (checkedValues: string[]) => {
		setSelectedTypes(checkedValues);
		handleSetQueryParams('types', checkedValues.join(','));
	};

	const handleMinimumTotalAvailableLengthChange = (value: number) => {
		setMinimumTotalAvailableLength(value);
		handleSetQueryParams('min_total_available_length', String(value));
	};

	const handleOuterDiameterChange = (value: [number, number]) => {
		setOuterDiameterRange(value);
	};

	const handleOuterDiameterChangeComplete = (value: [number, number]) => {
		setOuterDiameterRange(value);
		handleSetQueryParams('outer_diameter__gte', String(value[0]));
		handleSetQueryParams('outer_diameter__lte', String(value[1]));
	};

	const handleHeightChange = (value: [number, number]) => {
		setHeightRange(value);
	};

	const handleHeightChangeComplete = (value: [number, number]) => {
		setHeightRange(value);
		handleSetQueryParams('height__gte', String(value[0]));
		handleSetQueryParams('height__lte', String(value[1]));
	};

	const handleWidthChange = (value: [number, number]) => {
		setWidthRange(value);
	};

	const handleWidthChangeComplete = (value: [number, number]) => {
		setWidthRange(value);
		handleSetQueryParams('width__gte', String(value[0]));
		handleSetQueryParams('width__lte', String(value[1]));
	};

	const handleInnerDiameterChange = (value: [number, number]) => {
		setInnerDiameterRange(value);
	};

	const handleInnerDiameterChangeComplete = (value: [number, number]) => {
		setInnerDiameterRange(value);
		handleSetQueryParams('inner_diameter__gte', String(value[0]));
		handleSetQueryParams('inner_diameter__lte', String(value[1]));
	};

	const handleWallThicknessChange = (value: [number, number]) => {
		setWallThicknessRange(value);
	};

	const handleWallThicknessChangeComplete = (value: [number, number]) => {
		setWallThicknessRange(value);
		handleSetQueryParams('wall_thickness__gte', String(value[0]));
		handleSetQueryParams('wall_thickness__lte', String(value[1]));
	};

	const getShapeNameById = (id: string | undefined | null) => {
		if (id) {
			return getEffectiveShapeName(props.shapes.find((x) => x.id === id)?.display_name);
		}
	};

	type LocationsGroupedByRack = Record<string, any[]>;
	const locationsGroupedByRack: LocationsGroupedByRack | undefined =
		props.locations?.reduce((acc: LocationsGroupedByRack, location: any) => {
			const rackName = location.shelf.rack.rack_name || 'Unknown Rack';
			if (!acc[rackName]) {
				acc[rackName] = [];
			}
			acc[rackName].push(location);
			return acc;
		}, {} as LocationsGroupedByRack);

	// Basic function that cheks the name of the material in the order line item
	// and selects material types that match a substring.
	useEffect(() => {
		if (props.lineItem?.material_name) {
			const material = props.lineItem.material_name.toLowerCase();

			const types: string[] = [];
			const categories = new Set<string>();

			// Split "material" into tokens (words)
			const tokens = material
				.split(/\s+/) // split on whitespace
				.filter(Boolean); // remove empty strings

			for (const type of props.types) {
				const displayNameLower = type.display_name.toLowerCase();

				// Check if the displayName includes **all** tokens
				const hasAllTokens = tokens.every((token) =>
					displayNameLower.includes(token)
				);

				if (hasAllTokens) {
					types.push(type.id);
					categories.add(type.material_category.id);
				}
			}

			// If there's exactly one category, set it
			if (categories.size === 1) {
				const category = Array.from(categories)[0];
				setSelectedCategory(category);
				handleSetQueryParams('category', category);
			}

			// Update types and query params
			setSelectedTypes([...types]);
			handleSetQueryParams('types', types.join(','));
		}
	}, [props.types]);

	useEffect(() => {
		if (!selectedCategory) {
			setActiveKeys((prevState) => [...prevState, 'category']);
		}
		setActiveKeys((prevState) => [...prevState, 'type']);
	}, [selectedCategory]);

	useEffect(() => {
		if (selectedCategory && selectedTypes.length === 0) {
			setActiveKeys((prevState) => [...prevState, 'type']);
		}
	}, [selectedTypes]);

	useEffect(() => {
		if (!props.shape_selected && props.shapes.length > 0 && !search) {
			props.shapes.forEach((shape) => {
				if (shape.display_name === RECTANGLE_BAR) {
					setSelectedShape(shape.id);
					handleSetQueryParams('shape', shape.id);
				}
			});
		}
	}, [props.shapes]);

	useEffect(() => {
		setSelectedCategory(props.category_selected);
	}, [props.category_selected]);

	useEffect(() => {
		const newTypesArray = props.types_selected
			? props.types_selected.split(',')
			: [];
		setSelectedTypes(newTypesArray);
	}, [props.types_selected]);

	useEffect(() => {
		const currentUrlShelves = props.location_id_selected
			? props.location_id_selected.split(',')
			: [];
		setSelectedLocations(currentUrlShelves);
	}, [props.location_id_selected]);

	useEffect(() => {
		setBundleFriendlyId(props.bundle_friendly_id || '');
	}, [props.bundle_friendly_id]);

	useEffect(() => {
		if (props.heightRange) {
			setInitialHeightRange([props.heightRange.min, props.heightRange.max]);
			setHeightRange([
				Math.max(heightRange[0], props.heightRange.min),
				Math.min(heightRange[1], props.heightRange.max),
			]);
		}
	}, [props.heightRange]);

	useEffect(() => {
		if (props.widthRange) {
			setInitialWidthRange([props.widthRange.min, props.widthRange.max]);
			setWidthRange([
				Math.max(widthRange[0], props.widthRange.min),
				Math.min(widthRange[1], props.widthRange.max),
			]);
		}
	}, [props.widthRange]);

	useEffect(() => {
		if (props.totalAvailableLengthRange) {
			setMinTotalAvailableLengthMin(props.totalAvailableLengthRange.min);
			setMinTotalAvailableLengthMax(props.totalAvailableLengthRange.max);
		}
	}, [props.totalAvailableLengthRange]);

	useEffect(() => {
		const urlMin = Number(queryParams.get('height__gte'));
		const urlMax = Number(queryParams.get('height__lte'));
		const min = urlMin || props.heightRange?.min || 0;
		const max = urlMax || props.heightRange?.max || 999;
		setHeightRange([min, max]);
	}, [
		queryParams.get('height__gte'),
		queryParams.get('height__lte'),
		props.heightRange,
	]);

	useEffect(() => {
		const urlMin = Number(queryParams.get('width__gte'));
		const urlMax = Number(queryParams.get('width__lte'));
		const min = urlMin || props.widthRange?.min || 0;
		const max = urlMax || props.widthRange?.max || 999;
		setWidthRange([min, max]);
	}, [
		queryParams.get('width__gte'),
		queryParams.get('width__lte'),
		props.widthRange,
	]);

	useEffect(() => {
		const urlValue = queryParams.get('min_total_available_length');
		setMinimumTotalAvailableLength(urlValue ? Number(urlValue) : null);
	}, [queryParams.get('min_total_available_length')]);

	useEffect(() => {
		if (props.heightRange) {
			setInitialHeightRange([props.heightRange.min, props.heightRange.max]);
		}
	}, [props.heightRange]);

	useEffect(() => {
		if (props.widthRange) {
			setInitialWidthRange([props.widthRange.min, props.widthRange.max]);
		}
	}, [props.widthRange]);

	useEffect(() => {
		const outer_diameter__gte_str = queryParams.get('outer_diameter__gte');
		const outer_diameter__lte_str = queryParams.get('outer_diameter__lte');

		const url_min = outer_diameter__gte_str
			? Number(outer_diameter__gte_str)
			: null;
		const url_max = outer_diameter__lte_str
			? Number(outer_diameter__lte_str)
			: null;

		const fallback_min = props.outerDiameterRange?.min ?? 0;
		const fallback_max = props.outerDiameterRange?.max ?? 999;

		const newMin = url_min !== null ? url_min : fallback_min;
		const newMax = url_max !== null ? url_max : fallback_max;

		setOuterDiameterRange([newMin, newMax]);
	}, [
		queryParams.get('outer_diameter__gte'),
		queryParams.get('outer_diameter__lte'),
	]);

	useEffect(() => {
		if (props.outerDiameterRange) {
			const min = props.outerDiameterRange.min ?? 0;
			const max = props.outerDiameterRange.max ?? 999;

			setInitialOuterDiameterRange([min, max]);

			const urlMin = queryParams.get('outer_diameter__gte');
			const urlMax = queryParams.get('outer_diameter__lte');

			if (!urlMin && !urlMax) {
				setOuterDiameterRange([min, max]);
			}
		}
	}, [props.outerDiameterRange]);

	useEffect(() => {
		if (props.innerDiameterRange) {
			const min = props.innerDiameterRange.min ?? 0;
			const max = props.innerDiameterRange.max ?? 999;

			setInitialInnerDiameterRange([min, max]);
			const urlMin = queryParams.get('inner_diameter__gte');
			const urlMax = queryParams.get('inner_diameter__lte');

			if (!urlMin && !urlMax) {
				setInnerDiameterRange([min, max]);
			}
		}
	}, [props.innerDiameterRange]);

	useEffect(() => {
		const inner_diameter__gte_str = queryParams.get('inner_diameter__gte');
		const inner_diameter__lte_str = queryParams.get('inner_diameter__lte');

		const fallback_min = initialInnerDiameterRange[0];
		const fallback_max = initialInnerDiameterRange[1];

		const newMin = inner_diameter__gte_str
			? Number(inner_diameter__gte_str)
			: fallback_min;

		const newMax = inner_diameter__lte_str
			? Number(inner_diameter__lte_str)
			: fallback_max;

		setInnerDiameterRange([newMin, newMax]);
	}, [
		queryParams.get('inner_diameter__gte'),
		queryParams.get('inner_diameter__lte'),
	]);

	useEffect(() => {
		if (props.wallThicknessRange) {
			const min = props.wallThicknessRange.min ?? 0;
			const max = props.wallThicknessRange.max ?? 999;

			setInitialWallThicknessRange([min, max]);

			const urlMin = queryParams.get('wall_thickness__gte');
			const urlMax = queryParams.get('wall_thickness__lte');

			if (!urlMin && !urlMax) {
				setWallThicknessRange([min, max]);
			}
		}
	}, [props.wallThicknessRange]);

	useEffect(() => {
		const wall_thickness__gte_str = queryParams.get('wall_thickness__gte');
		const wall_thickness__lte_str = queryParams.get('wall_thickness__lte');

		const fallback_min = initialWallThicknessRange[0];
		const fallback_max = initialWallThicknessRange[1];

		const newMin = wall_thickness__gte_str
			? Number(wall_thickness__gte_str)
			: fallback_min;

		const newMax = wall_thickness__lte_str
			? Number(wall_thickness__lte_str)
			: fallback_max;

		setWallThicknessRange([newMin, newMax]);
	}, [
		queryParams.get('wall_thickness__gte'),
		queryParams.get('wall_thickness__lte'),
	]);

	useEffect(() => {
		if (
			props.has_certificate === 'True' ||
			props.has_certificate === 'has_cert'
		) {
			setCertificateFilter('has_cert');
		} else if (
			props.has_certificate === 'False' ||
			props.has_certificate === 'no_cert'
		) {
			setCertificateFilter('no_cert');
		} else {
			setCertificateFilter('all');
		}
	}, [props.has_certificate]);

	// Initial range values
	const [initialHeightRange, setInitialHeightRange] = useState<
		[number, number]
	>([0, 999]);
	const [initialWidthRange, setInitialWidthRange] = useState<[number, number]>([
		0, 999,
	]);
	const [initialInnerDiameterRange, setInitialInnerDiameterRange] = useState<
		[number, number]
	>([0, 999]);
	const [initialOuterDiameterRange, setInitialOuterDiameterRange] = useState<
		[number, number]
	>([0, 999]);
	const [initialWallThicknessRange, setInitialWallThicknessRange] = useState<
		[number, number]
	>([0, 999]);
	const [minTotalAvailableLengthMin, setMinTotalAvailableLengthMin] =
		useState<number>(props.totalAvailableLengthRange?.min || 0);

	const [minTotalAvailableLengthMax, setMinTotalAvailableLengthMax] =
		useState<number>(props.totalAvailableLengthRange?.max || 1000);

	return (
		<div
			style={{
				background: '#FBFBFB',
				border: '1px solid #D9D9D9',
				borderRadius: '2px',
				flex: '0 0 250px',
			}}
		>
			<div
				style={{
					background: '#FBFBFB',
					borderBottom: '1px solid #D9D9D9',
					borderRadius: '2px',
				}}
			>
				<div className="flex justify-between items-center pl-3 py-1">
					<Text strong style={{ margin: 0 }}>
						Filters
					</Text>
					<div>
						<Button type="link" onClick={handleResetFilters}>
							Reset
						</Button>
					</div>
				</div>
			</div>
			<div>
				<Collapse
					activeKey={activeKeys}
					onChange={(keys) => setActiveKeys(keys)}
					style={{ border: 'none' }}
					ghost={true}
				>
				{showBuildingFilter && (
					<Panel
						header={
							<div className="flex flex-row justify-between w-full">
								Building
								<a
									onClick={(e) => {
										e.stopPropagation();
										handleDeleteQueryParams('building');
									}}
								>
									Reset
								</a>
							</div>
						}
						key="building"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Select
							mode="multiple"
							placeholder="All Buildings"
							allowClear
							style={{ width: '100%' }}
							value={queryParams.get('building') ? queryParams.get('building')!.split(',') : []}
							onChange={(values: string[]) => {
								if (values.length > 0) {
									handleSetQueryParams('building', values.join(','));
								} else {
									handleDeleteQueryParams('building');
								}
							}}
						>
							{buildings.map((b) => (
								<Option key={b.id} value={b.id}>
									{b.name}
								</Option>
							))}
						</Select>
					</Panel>
				)}
				{props.availability_status ? (
					<Panel
						header="Availability Status"
						key="availability_status"
							style={{
								width: '100%',
								backgroundColor: 'white',
								borderBottom: '1px solid #d9d9d9',
							}}
						>
							<Checkbox.Group
								onChange={handleStatusChange}
								value={selectedStatus}
							>
								<div style={{ marginBottom: '8px', width: '100%' }}>
									<Checkbox value="Available">Available</Checkbox>
								</div>
								<div style={{ marginBottom: '8px', width: '100%' }}>
									<Checkbox value="Unavailable">Unavailable</Checkbox>
								</div>
							</Checkbox.Group>
						</Panel>
					) : null}
					<Panel
						header={
							<div className="flex flex-row justify-between w-full">
								Bundle | Length ID
								<a
									onClick={(e) => {
										e.stopPropagation();
										setBundleFriendlyId('');
										handleDeleteQueryParams('bundle_friendly_id');
									}}
								>
									Reset
								</a>
							</div>
						}
						key="bundle_id"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Form.Item>
							<Input
								placeholder="Enter Bundle | Length ID"
								value={bundleFriendlyId}
								onChange={handleBundleFriendlyIdChange}
								onPressEnter={handleApplyBundleFriendlyId}
								suffix={
									<Button
										type="link"
										size="small"
										onClick={handleApplyBundleFriendlyId}
									>
										Apply
									</Button>
								}
							/>
						</Form.Item>
					</Panel>

					<Panel
						header="Vendor"
						key="vendor"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Checkbox.Group
							value={selectedVendors}
							onChange={handleVendorChange}
						>
							<div className="flex flex-col">
								{' '}
								{/* Asegura que los elementos estén en una columna */}
								{props.vendors.map((x) => (
									<div key={x.id} className="mb-2">
										<Checkbox value={x.id}>{x.display_name}</Checkbox>
									</div>
								))}
							</div>
						</Checkbox.Group>
					</Panel>

					<Panel
						header="Shape"
						key="shape"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Radio.Group value={selectedShape} onChange={handleShapeChange}>
							{props.shapes.map((x) => (
								<div key={x.id} style={{ marginBottom: '8px' }}>
									<Radio value={x.id}>{x.display_name}</Radio>
								</div>
							))}
						</Radio.Group>
					</Panel>

					<Panel
						header={
							<div className="flex flex-row justify-between w-full">
								Category
								<a
									onClick={(e) => {
										e.stopPropagation();
										setSelectedCategory('');
										handleDeleteQueryParams('category');
									}}
								>
									Reset
								</a>
							</div>
						}
						key="category"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Radio.Group
							value={selectedCategory}
							onChange={handleCategoryChange}
						>
							{props.categories.map((x) => (
								<div key={x.id} style={{ marginBottom: '8px' }}>
									<Radio value={x.id}>{x.display_name}</Radio>
								</div>
							))}
						</Radio.Group>
					</Panel>

					<Panel
						header="Type"
						key="type"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<div className="mb-3">
							<Input
								placeholder="Search types"
								value={searchTypesValue}
								onChange={handleSearchChange}
							/>
						</div>

						{selectedCategory ? (
							<Scrollbars style={{ height: 250 }}>
								<Checkbox.Group
									value={selectedTypes}
									onChange={handleTypeChange}
								>
									{filteredTypes.map((type) => (
										<div
											key={type.id}
											style={{ marginBottom: '8px', width: '100%' }}
										>
											<Checkbox value={type.id}>{type.display_name}</Checkbox>
										</div>
									))}
								</Checkbox.Group>
							</Scrollbars>
						) : null}
					</Panel>

					{/* Shelf Filter */}
					<Panel
						header={
							<div className="flex flex-row justify-between w-full">
								Location
								<a
									onClick={(e) => {
										e.stopPropagation();
										setSelectedLocations([]);
										handleDeleteQueryParams('location_id');
										props.resetFiltersSignal && props.resetFiltersSignal();
									}}
								>
									Reset
								</a>
							</div>
						}
						key="shelf"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Select
							mode="multiple"
							placeholder="Select Locations"
							value={selectedLocations}
							onChange={setSelectedLocations}
							style={{ width: '100%', marginBottom: '8px' }}
							maxTagCount="responsive"
							showSearch
							onSearch={props.onSearchLocation}
							notFoundContent={props.loading ? 'Searching...' : 'No results'}
							filterOption={false}
						>
							{locationsGroupedByRack &&
								Object.entries(locationsGroupedByRack).map(
									([rackName, locations]) => (
										<Select.OptGroup key={rackName} label={rackName}>
											{locations.map((location) => (
												<Option key={location.id} value={location.id}>
													{formatLocation(location.display_name)}
												</Option>
											))}
										</Select.OptGroup>
									)
								)}
						</Select>
						<Button
							type="primary"
							block
							onClick={() => {
								if (selectedLocations.length > 0) {
									handleSetQueryParams(
										'location_id',
										selectedLocations.join(',')
									);
								} else {
									handleDeleteQueryParams('location_id');
								}
							}}
							disabled={selectedLocations.length === 0}
						>
							Apply
						</Button>
					</Panel>

					{(getShapeNameById(selectedShape) === RECTANGLE_BAR ||
						getShapeNameById(selectedShape) === RECTANGLE_TUBE ||
						getShapeNameById(selectedShape) === PLATE ||
						getShapeNameById(selectedShape) === ANGLE ||
						getShapeNameById(selectedShape) === CHANNEL ||
						getShapeNameById(selectedShape) === HEX_BAR) && (
							<>
								<Panel
									header={
										<div className="flex flex-row justify-between w-full">
											Height
											<a
												onClick={(e) => {
													e.stopPropagation();
													setHeightRange(initialHeightRange);
													handleDeleteQueryParams('height__gte');
													handleDeleteQueryParams('height__lte');
												}}
											>
												Reset
											</a>
										</div>
									}
									key="height"
									style={{
										width: '100%',
										backgroundColor: 'white',
										borderBottom: '1px solid #d9d9d9',
									}}
								>
									<Row gutter={8} align="middle">
										<Col span={12}>
											<Form.Item label="Min">
												<InputNumber
													min={initialHeightRange[0]}
													max={initialHeightRange[1]}
													step={0.25}
													value={heightRange[0]}
													onChange={(value) => {
														if (value) {
															setHeightRange([Number(value), heightRange[1]]);
															handleHeightChange([Number(value), heightRange[1]]);
														}
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															handleHeightChangeComplete([
																heightRange[0],
																heightRange[1],
															]);
														}
													}}
													style={{ width: '100%' }}
													suffix="in"
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Max">
												<InputNumber
													min={initialHeightRange[0]}
													max={initialHeightRange[1]}
													step={0.25}
													value={heightRange[1]}
													onChange={(value) => {
														if (value) {
															setHeightRange([heightRange[0], Number(value)]);
															handleHeightChange([heightRange[0], Number(value)]);
														}
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															handleHeightChangeComplete([
																heightRange[0],
																heightRange[1],
															]);
														}
													}}
													style={{ width: '100%' }}
													suffix="in"
												/>
											</Form.Item>
										</Col>
										<Col span={24}>
											<Slider
												range
												min={initialHeightRange[0]}
												max={initialHeightRange[1]}
												step={0.25}
												value={heightRange}
												onChange={(value) => {
													handleHeightChange([value[0], value[1]]);
												}}
												tooltip={{ formatter: (value) => `${value}"` }}
											/>
										</Col>
										<Col span={24} style={{ marginTop: '10px' }}>
											<Button
												type="primary"
												block
												onClick={() => {
													handleHeightChangeComplete([
														heightRange[0],
														heightRange[1],
													]);
												}}
											>
												Apply
											</Button>
										</Col>
									</Row>
								</Panel>
								<Panel
									header={
										<div className="flex flex-row justify-between w-full">
											Width
											<a
												onClick={(e) => {
													e.stopPropagation();
													setWidthRange(initialWidthRange);
													handleDeleteQueryParams('width__gte');
													handleDeleteQueryParams('width__lte');
												}}
											>
												Reset
											</a>
										</div>
									}
									key="width"
									style={{
										width: '100%',
										backgroundColor: 'white',
										borderBottom: '1px solid #d9d9d9',
									}}
								>
									<Row gutter={8} align="middle">
										<Col span={12}>
											<Form.Item label="Min">
												<InputNumber
													min={initialWidthRange[0]}
													max={initialWidthRange[1]}
													step={0.25}
													value={widthRange[0]}
													onChange={(value) => {
														if (value) {
															setWidthRange([Number(value), widthRange[1]]);
															handleWidthChange([Number(value), widthRange[1]]);
														}
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															handleWidthChangeComplete([
																widthRange[0],
																widthRange[1],
															]);
														}
													}}
													style={{ width: '100%' }}
													suffix="in"
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Max">
												<InputNumber
													min={initialWidthRange[0]}
													max={initialWidthRange[1]}
													step={0.25}
													value={widthRange[1]}
													onChange={(value) => {
														if (value) {
															setWidthRange([widthRange[0], Number(value)]);
															handleWidthChange([widthRange[0], Number(value)]);
														}
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															handleWidthChangeComplete([
																widthRange[0],
																widthRange[1],
															]);
														}
													}}
													style={{ width: '100%' }}
													suffix="in"
												/>
											</Form.Item>
										</Col>
										<Col span={24}>
											<Slider
												range
												min={initialWidthRange[0]}
												max={initialWidthRange[1]}
												step={0.25}
												value={widthRange}
												onChange={(value) => {
													handleWidthChange([value[0], value[1]]);
												}}
												tooltip={{ formatter: (value) => `${value}"` }}
											/>
										</Col>
										<Col span={24} style={{ marginTop: '10px' }}>
											<Button
												type="primary"
												block
												onClick={() => {
													handleWidthChangeComplete([
														widthRange[0],
														widthRange[1],
													]);
												}}
											>
												Apply
											</Button>
										</Col>
									</Row>
								</Panel>
							</>
						)}

					{getShapeNameById(selectedShape) === ROUND_BAR ||
						getShapeNameById(selectedShape) === ROUND_TUBE ? (
						<Panel
							header={
								<div className="flex flex-row justify-between w-full">
									Outer Diameter
									<a
										onClick={(e) => {
											e.stopPropagation();
											setOuterDiameterRange(initialOuterDiameterRange);
											handleDeleteQueryParams('outer_diameter__gte');
											handleDeleteQueryParams('outer_diameter__lte');
										}}
									>
										Reset
									</a>
								</div>
							}
							key="outer_diameter"
							style={{
								width: '100%',
								backgroundColor: 'white',
								borderBottom: '1px solid #d9d9d9',
							}}
						>
							<Row gutter={8} align="middle">
								<Col span={12}>
									<Form.Item label="Min">
										<InputNumber
											min={initialOuterDiameterRange[0]}
											max={initialOuterDiameterRange[1]}
											step={0.25}
											value={outerDiameterRange[0]}
											onChange={(value) => {
												setOuterDiameterRange([
													Number(value),
													outerDiameterRange[1],
												]);
												handleOuterDiameterChange([
													Number(value),
													outerDiameterRange[1],
												]);
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleOuterDiameterChangeComplete([
														outerDiameterRange[0],
														outerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Max">
										<InputNumber
											min={initialOuterDiameterRange[0]}
											max={initialOuterDiameterRange[1]}
											step={0.25}
											value={outerDiameterRange[1]}
											onChange={(value) => {
												if (value) {
													setOuterDiameterRange([
														outerDiameterRange[0],
														Number(value),
													]);
													handleOuterDiameterChange([
														outerDiameterRange[0],
														Number(value),
													]);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleOuterDiameterChangeComplete([
														outerDiameterRange[0],
														outerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={24}>
									<Slider
										range
										min={initialOuterDiameterRange[0]}
										max={initialOuterDiameterRange[1]}
										step={0.25}
										value={outerDiameterRange}
										onChange={(value) => {
											handleOuterDiameterChange([value[0], value[1]]);
										}}
										tooltip={{ formatter: (value) => `${value}"` }}
									/>
								</Col>
								<Col span={24} style={{ marginTop: '10px' }}>
									<Button
										type="primary"
										block
										onClick={() => {
											handleOuterDiameterChangeComplete([
												outerDiameterRange[0],
												outerDiameterRange[1],
											]);
										}}
									>
										Apply
									</Button>
								</Col>
							</Row>
						</Panel>
					) : null}

					{getShapeNameById(selectedShape) === ROUND_TUBE && (
						<Panel
							header={
								<div className="flex flex-row justify-between w-full">
									Inner Diameter
									<a
										onClick={(e) => {
											e.stopPropagation();
											setInnerDiameterRange(initialInnerDiameterRange);
											handleDeleteQueryParams('inner_diameter__gte');
											handleDeleteQueryParams('inner_diameter__lte');
										}}
									>
										Reset
									</a>
								</div>
							}
							key="inner_diameter"
							style={{
								width: '100%',
								backgroundColor: 'white',
								borderBottom: '1px solid #d9d9d9',
							}}
						>
							<Row gutter={8} align="middle">
								<Col span={12}>
									<Form.Item label="Min">
										<InputNumber
											min={initialInnerDiameterRange[0]}
											max={initialInnerDiameterRange[1]}
											step={0.25}
											value={innerDiameterRange[0]}
											onChange={(value) => {
												if (value) {
													setInnerDiameterRange([
														Number(value),
														innerDiameterRange[1],
													]);
													handleInnerDiameterChange([
														Number(value),
														innerDiameterRange[1],
													]);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleInnerDiameterChangeComplete([
														innerDiameterRange[0],
														innerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Max">
										<InputNumber
											min={initialInnerDiameterRange[0]}
											max={initialInnerDiameterRange[1]}
											step={0.25}
											value={innerDiameterRange[1]}
											onChange={(value) => {
												if (value) {
													setInnerDiameterRange([
														innerDiameterRange[0],
														Number(value),
													]);
													handleInnerDiameterChange([
														innerDiameterRange[0],
														Number(value),
													]);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleInnerDiameterChangeComplete([
														innerDiameterRange[0],
														innerDiameterRange[1],
													]);
												}
											}}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
								<Col span={24}>
									<Slider
										range
										min={initialInnerDiameterRange[0]}
										max={initialInnerDiameterRange[1]}
										step={0.25}
										value={innerDiameterRange}
										onChange={(value) => {
											handleInnerDiameterChange([value[0], value[1]]);
										}}
										tooltip={{ formatter: (value) => `${value}"` }}
									/>
								</Col>
								<Col span={24} style={{ marginTop: '10px' }}>
									<Button
										type="primary"
										block
										onClick={() => {
											handleInnerDiameterChangeComplete([
												innerDiameterRange[0],
												innerDiameterRange[1],
											]);
										}}
									>
										Apply
									</Button>
								</Col>
							</Row>
						</Panel>
					)}

					{(getShapeNameById(selectedShape) === ANGLE ||
						getShapeNameById(selectedShape) === CHANNEL ||
						getShapeNameById(selectedShape) === RECTANGLE_TUBE) && (
							<>
								<Panel
									header={
										<div className="flex flex-row justify-between w-full">
											Wall Thickness
											<a
												onClick={(e) => {
													e.stopPropagation();
													setWallThicknessRange(initialWallThicknessRange);
													handleDeleteQueryParams('wall_thickness__gte');
													handleDeleteQueryParams('wall_thickness__lte');
												}}
											>
												Reset
											</a>
										</div>
									}
									key="wall_thickness"
									style={{
										width: '100%',
										backgroundColor: 'white',
										borderBottom: '1px solid #d9d9d9',
									}}
								>
									<Row gutter={8} align="middle">
										<Col span={12}>
											<Form.Item label="Min">
												<InputNumber
													min={initialWallThicknessRange[0]}
													max={initialWallThicknessRange[1]}
													step={0.25}
													value={wallThicknessRange[0]}
													onChange={(value) => {
														if (value) {
															setWallThicknessRange([
																Number(value),
																wallThicknessRange[1],
															]);
															handleWallThicknessChange([
																Number(value),
																wallThicknessRange[1],
															]);
														}
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															handleWallThicknessChangeComplete([
																wallThicknessRange[0],
																wallThicknessRange[1],
															]);
														}
													}}
													style={{ width: '100%' }}
													suffix="in"
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Max">
												<InputNumber
													min={initialWallThicknessRange[0]}
													max={initialWallThicknessRange[1]}
													step={0.25}
													value={wallThicknessRange[1]}
													onChange={(value) => {
														if (value) {
															setWallThicknessRange([
																wallThicknessRange[0],
																Number(value),
															]);
															handleWallThicknessChange([
																wallThicknessRange[0],
																Number(value),
															]);
														}
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															handleWallThicknessChangeComplete([
																wallThicknessRange[0],
																wallThicknessRange[1],
															]);
														}
													}}
													style={{ width: '100%' }}
													suffix="in"
												/>
											</Form.Item>
										</Col>
										<Col span={24}>
											<Slider
												range
												min={initialWallThicknessRange[0]}
												max={initialWallThicknessRange[1]}
												step={0.25}
												value={wallThicknessRange}
												onChange={(value) => {
													handleWallThicknessChange([value[0], value[1]]);
												}}
												tooltip={{ formatter: (value) => `${value}"` }}
											/>
										</Col>
										<Col span={24} style={{ marginTop: '10px' }}>
											<Button
												type="primary"
												block
												onClick={() => {
													handleWallThicknessChangeComplete([
														wallThicknessRange[0],
														wallThicknessRange[1],
													]);
												}}
											>
												Apply
											</Button>
										</Col>
									</Row>
								</Panel>
							</>
						)}

					<Panel
						header="Minimum Available Length"
						key="min_available_length"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Form.Item>
							<InputNumber
								step={1}
								min={minTotalAvailableLengthMin}
								max={minTotalAvailableLengthMax}
								value={minimumTotalAvailableLength}
								onChange={(value) => {
									if (value) {
										setMinimumTotalAvailableLength(value);
									}
								}}
								onBlur={(e) => {
									if (e.target.value && minimumTotalAvailableLength) {
										handleMinimumTotalAvailableLengthChange(
											minimumTotalAvailableLength
										);
									}
								}}
								onPressEnter={() => {
									if (minimumTotalAvailableLength) {
										handleMinimumTotalAvailableLengthChange(
											minimumTotalAvailableLength
										);
									}
								}}
								style={{ width: '100%' }}
								suffix="in"
							/>
						</Form.Item>
					</Panel>
					<Panel
						header="Certificate"
						key="certificate"
						style={{
							width: '100%',
							backgroundColor: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<Select
							value={certificateFilter}
							onChange={handleCertificateChange}
							style={{ width: '100%' }}
							placeholder="Select certificate filter"
						>
							<Select.Option value="all">All</Select.Option>
							<Select.Option value="has_cert">Has Cert</Select.Option>
							<Select.Option value="no_cert">No Cert</Select.Option>
						</Select>
					</Panel>
				</Collapse>
			</div>
		</div>
	);
};
